import { logFocusError, } from "@single-js/common/core/sql/actions";
import { LELoading, LESuccess, } from "@single-js/common/util/loading-error";
import { nullableToMaybe } from "@single-js/common/util/nullable";
import { mapMultiSelectErrorResult, multiSelectParallel, multiSelectParallelWithErrors, multiSelectWithErrors, select, } from "@single-js/common/util/query";
import { SQLIdent, stripSlashG } from "@single-js/common/util/sql-query";
import { rawResultToArray } from "@single-js/common/util/sql-results";
import BigNumber from "@single-js/common/vendor/bignumber.js/bignumber";
import { differenceInSeconds } from "date-fns";
import _ from "lodash";
import { genFunctionId, genTableId } from "memsql/db-object-id";
import getShowCreateTableQuery from "memsql/get-show-create-table-query";
import { from, merge, Observable, of } from "rxjs";
import { logError } from "util/logging";
import * as logging from "util/logging";
import { makeActionCreator } from "worker/api/helpers";
import { SQL_AVAILABLE_MEMORY, SQL_DATABASE_DISK_USAGE, SQL_DATABASE_NAMES, SQL_DISK_USAGE, SQL_MEMORY_USAGE, SQL_STATISTICS_COLUMNS_COLUMNAR, SQL_STATISTICS_COLUMNS_MEMORY, SQL_STATISTICS_DATABASE, SQL_STATISTICS_HA, SQL_STATISTICS_INDEXES, SQL_STATISTICS_INDEXES_COLUMNAR, SQL_STATISTICS_TABLE, SQL_STRUCTURE_AGGREGATES, SQL_STRUCTURE_COLUMNS, SQL_STRUCTURE_DATABASES, SQL_STRUCTURE_FUNCTIONS, SQL_STRUCTURE_SHOW_TABLES, SQL_STRUCTURE_STORED_PROCEDURES, SQL_STRUCTURE_TABLES_SINGLE_QUERY, SQL_STRUCTURE_TABLES_SINGLE_QUERY_MIN_VERSION, SQL_TABLE_LIKE_SAMPLE, SQL_TABLES_COUNT, SQL_TOTAL_DISK, SQL_TOTAL_MEMORY, } from "worker/api/schema-queries";
import { fetchMemsqlVersion } from "worker/api/version-query";
function sortTables(tables) {
    return _.orderBy(tables, [(table) => table.tableId], "asc");
}
const createStructureUpdateAction = (data) => {
    return {
        type: "SCHEMA_STRUCTURE_UPDATE",
        error: false,
        payload: data,
    };
};
const createDatabasesStructureAction = (data) => {
    return {
        type: "DATABASES_STRUCTURE",
        error: false,
        payload: {
            loading: false,
            data,
        },
    };
};
const getTableType = (value) => {
    switch (value) {
        case "BASE TABLE":
            return "BASE";
        case "TEMPORARY TABLE":
            return "TEMPORARY";
        default:
            return "UNKNOWN";
    }
};
const getTableStorage = (value) => {
    switch (value) {
        case "INMEMORY_ROWSTORE":
            return value;
        case "COLUMNSTORE":
            return value;
        default:
            return "UNKNOWN";
    }
};
function parseSqlStructureTable({ databaseName, tableName, isSharded, tableType, tableStorage, }) {
    const id = genTableId({
        databaseName,
        tableName,
    });
    if (tableType === "VIEW" || tableType === "SYSTEM VIEW") {
        return {
            tableId: id,
            tableName,
            databaseName,
            kind: "VIEW",
        };
    }
    return {
        tableId: id,
        databaseName,
        tableName,
        kind: "TABLE",
        isSharded,
        tableType: getTableType(tableType),
        tableStorage: getTableStorage(tableStorage),
    };
}
function parseTableShowTables(databaseName, row) {
    // the table name column is called "Tables_in_${databaseName}"
    const tableName = row[`Tables_in_${databaseName}`];
    return parseSqlStructureTable({
        databaseName,
        tableName,
        isSharded: Boolean(row.distributed),
        tableType: row.Table_type,
        tableStorage: row.Storage_type,
    });
}
function parseTableSingleQuery({ databaseName, tableName, tableType, isSharded, tableStorage, }) {
    return parseSqlStructureTable({
        databaseName,
        tableName,
        tableType,
        isSharded: isSharded.gt(0),
        tableStorage: nullableToMaybe(tableStorage),
    });
}
const queryTables = ({ conn, databaseName, }) => ([databases, columns]) => {
    // On newer versions of memSQL, we can obtain the information we want for
    // all tables in a single query joining two INFORMATION_SCHEMA tables. On
    // older versions, we have to run a separate query for each database.
    // We check the version instead of doing feature detection because this is
    // a performance optimization and querying INFORMATION_SCHEMA tables to do
    // the feature detection is too expensive, but querying the version is
    // cheap.
    return fetchMemsqlVersion(conn)
        .then((version) => {
        let queryFilter = "%";
        if (databaseName) {
            queryFilter = databaseName;
        }
        if (version.ge(SQL_STRUCTURE_TABLES_SINGLE_QUERY_MIN_VERSION)) {
            return select(conn, SQL_STRUCTURE_TABLES_SINGLE_QUERY, [
                queryFilter,
            ]).then((sqlStructureTables) => {
                return _.map(sqlStructureTables, parseTableSingleQuery);
            });
        }
        else {
            const tableQueries = _.map(databases, (db) => ({
                sql: SQL_STRUCTURE_SHOW_TABLES,
                params: [SQLIdent(db.databaseName)],
            }));
            return multiSelectWithErrors(conn, ...tableQueries).then((results) => _.flatMap(results, (rows, idx) => {
                if (_.isError(rows)) {
                    if (rows.code === "ER_DBACCESS_DENIED_ERROR") {
                        return [];
                    }
                    else {
                        throw rows;
                    }
                }
                return _.map(rows, (row) => parseTableShowTables(databases[idx].databaseName, row));
            }));
        }
    })
        .then((tables) => [columns, tables]);
};
const queryFunctions = (conn) => ({ databaseNames, }) => {
    const functionQueries = _.map(databaseNames, (databaseName) => ({
        sql: SQL_STRUCTURE_FUNCTIONS,
        params: [SQLIdent(databaseName)],
    }));
    return multiSelectWithErrors(conn, ...functionQueries).then((resultSets) => {
        const userDefinedFunctions = [];
        // `resultSets` is an array of result sets, and each result set is an
        // array of rows.
        _.each(resultSets, (rows, idx) => {
            if (_.isError(rows)) {
                if (rows.code === "ER_DBACCESS_DENIED_ERROR") {
                    return;
                }
                else {
                    throw rows;
                }
            }
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                // the table name column is called "Functions_in_${databaseName}"
                const databaseName = databaseNames[idx];
                const name = r[`Functions_in_${databaseName}`];
                const functionId = genFunctionId({
                    databaseName,
                    functionName: name,
                });
                const definer = r.Definer;
                switch (r["Function Type"]) {
                    case "User Defined Function": {
                        userDefinedFunctions.push({
                            kind: "USER_DEFINED_FUNCTION",
                            databaseName,
                            name,
                            type: "SCALAR_VALUED_FUNCTION",
                            functionId,
                            definer,
                        });
                        break;
                    }
                    case "Table Valued Function": {
                        userDefinedFunctions.push({
                            kind: "USER_DEFINED_FUNCTION",
                            databaseName,
                            name,
                            type: "TABLE_VALUED_FUNCTION",
                            functionId,
                            definer,
                        });
                        break;
                    }
                    case "Stored Procedure": {
                        // Ignore stored procedures shown in the output of SHOW
                        // FUNCTIONS since we grab those from SHOW STORED
                        // PROCEDURES. Only version of MemSQL older than 6.7.11
                        // show Stored Procedures in the output of SHOW
                        // FUNCTIONS.
                        break;
                    }
                    default: {
                        logError(new Error(`Found a function with unknown type: ${r["Function Type"]}`));
                    }
                }
            }
        });
        return userDefinedFunctions;
    });
};
const queryAggregates = (conn) => ({ databaseNames, }) => {
    const aggregateQueries = _.map(databaseNames, (databaseName) => ({
        sql: SQL_STRUCTURE_AGGREGATES,
        params: [SQLIdent(databaseName)],
    }));
    return multiSelectWithErrors(conn, ...aggregateQueries).then((resultSets) => {
        return _.flatMap(resultSets, (rows, idx) => {
            if (_.isError(rows)) {
                if (rows.code === "ER_DBACCESS_DENIED_ERROR") {
                    return [];
                }
                else {
                    throw rows;
                }
            }
            return _.map(rows, (row) => {
                const databaseName = databaseNames[idx];
                // the table name column is called "Aggregates_in_${databaseName}"
                const name = row[`Aggregates_in_${databaseName}`];
                const functionId = genTableId({
                    databaseName,
                    tableName: name,
                });
                return {
                    kind: "USER_DEFINED_AGGREGATE",
                    functionId,
                    databaseName,
                    name,
                };
            });
        });
    });
};
const queryStoredProcedures = (conn) => ({ databaseNames, }) => {
    const storedProcedureQueries = _.map(databaseNames, (databaseName) => ({
        sql: SQL_STRUCTURE_STORED_PROCEDURES,
        params: [SQLIdent(databaseName)],
    }));
    return multiSelectWithErrors(conn, ...storedProcedureQueries).then((resultSets) => {
        return _.flatMap(resultSets, (rows, idx) => {
            if (_.isError(rows)) {
                if (rows.code === "ER_DBACCESS_DENIED_ERROR") {
                    return [];
                }
                else {
                    throw rows;
                }
            }
            const storedProcedures = [];
            _.each(rows, (row) => {
                const databaseName = databaseNames[idx];
                // the table name column is called "Procedures_in_${databaseName}" or
                // "Functions_in_${databaseName}" depending on the MemSQL version
                const name = row[`Procedures_in_${databaseName}`] ||
                    row[`Functions_in_${databaseName}`];
                // On versions prior to 6.7.11, SHOW PROCEDURES can
                // actually return UDFs so we need to check if the
                // "Function Type" column exists and if it does, we
                // need to disregard this row if its type is not
                // "Stored Procedure".
                if (row["Function Type"] &&
                    row["Function Type"] !== "Stored Procedure") {
                    return;
                }
                const functionId = genTableId({
                    databaseName,
                    tableName: name,
                });
                storedProcedures.push({
                    kind: "STORED_PROCEDURE",
                    databaseName,
                    definer: row.Definer,
                    name,
                    functionId,
                });
            });
            return storedProcedures;
        });
    });
};
function fetchSchemaStructure({ getConnection, databaseName, }) {
    let queryFilter = "%";
    if (databaseName) {
        queryFilter = databaseName;
    }
    return multiSelectParallel({
        getConnection,
        queries: [
            { sql: SQL_STRUCTURE_DATABASES, params: [queryFilter] },
            { sql: SQL_STRUCTURE_COLUMNS, params: [queryFilter] },
        ],
    })
        .then((results) => {
        return Promise.all([getConnection(), results]);
    })
        .then(([conn, results]) => {
        return queryTables({ conn, databaseName })(results)
            .then(([sqlColumns, tableLikes,]) => {
            const views = _.filter(tableLikes, (tableLike) => tableLike.kind === "VIEW");
            const tables = _.filter(tableLikes, (tableLike) => tableLike.kind === "TABLE");
            const columns = [];
            const viewColumns = [];
            // Loop through columns and split view columns from regular columns.
            for (let i = 0; i < sqlColumns.length; i++) {
                const column = sqlColumns[i];
                const view = _.find(views, (view) => view.tableName === column.tableName &&
                    view.databaseName ===
                        column.databaseName);
                if (view) {
                    // view column
                    viewColumns.push({
                        databaseName: column.databaseName,
                        tableName: view.tableName,
                        columnName: column.columnName,
                        columnId: column.columnId,
                        kind: "VIEW_COLUMN",
                        subType: column.subType,
                        dataType: column.dataType,
                        // We cast ordinal position to number since it will never exceed 2^53.
                        ordinalPosition: column.ordinalPosition.toNumber(),
                        isNullable: column.isNullable.eq(1),
                        autoIncrement: column.autoIncrement.eq(1),
                    });
                }
                else {
                    // regular column
                    const table = _.find(tables, (table) => table.tableName ===
                        column.tableName &&
                        table.databaseName ===
                            column.databaseName);
                    if (!table) {
                        // We found a column for which there is neither a
                        // view nor a table. This means that either:
                        // - this column corresponds to a table that was
                        //   deleted while the Schema Explorer was issuing
                        // - this column corresponds to a TVF function since
                        //   MemSQL will add TVF function columns to
                        //   `information_schema.columns` and TVF functions
                        //   as tables in `information_schema.tables` but
                        //   not to `SHOW TABLES IN <> EXTENDED` which is
                        //   what Studio uses to read the list of tables and
                        //   views. In either case, we want to ignore this
                        //   column and just keep going without pushing
                        //   anything into either the `columns` or
                        //   `viewColumns` arrays.
                        continue;
                    }
                    columns.push({
                        databaseName: column.databaseName,
                        tableName: column.tableName,
                        columnName: column.columnName,
                        columnId: column.columnId,
                        kind: "TABLE_COLUMN",
                        dataType: column.dataType,
                        // We cast ordinal position to number since it will never exceed 2^53.
                        ordinalPosition: column.ordinalPosition.toNumber(),
                        subType: column.subType,
                        computed: column.computed.eq(1),
                        defaultValueSQL: nullableToMaybe(column.defaultValueSQL),
                        columnStorage: table.tableStorage,
                        isNullable: column.isNullable.eq(1),
                        autoIncrement: column.autoIncrement.eq(1),
                    });
                }
            }
            // Structure tables and views by indexing them per database
            let allTablesPerDatabase = {};
            let allViewsPerDatabase = {};
            const tablesSorted = sortTables(tables);
            const [databases] = results;
            databases.forEach((database) => {
                const { databaseName } = database;
                const tablesPerDatabase = tablesSorted.filter((table) => table.databaseName === databaseName);
                allTablesPerDatabase = {
                    ...allTablesPerDatabase,
                    [databaseName]: new LESuccess(tablesPerDatabase),
                };
                const viewsPerDatabase = views.filter((view) => view.databaseName === databaseName);
                allViewsPerDatabase = {
                    ...allViewsPerDatabase,
                    [databaseName]: new LESuccess(viewsPerDatabase),
                };
            });
            return {
                tables: allTablesPerDatabase,
                views: allViewsPerDatabase,
                columns: new LESuccess(columns),
                viewColumns: new LESuccess(viewColumns),
                aggregates: new LELoading(),
                userDefinedFunctions: new LELoading(),
                storedProcedures: new LELoading(),
            };
        })
            .finally(() => {
            conn.release();
        });
    });
}
function fetchDatabasesStructure(getConnection) {
    return multiSelectParallel({
        getConnection,
        queries: [{ sql: SQL_TABLES_COUNT }, { sql: SQL_STATISTICS_DATABASE }],
    }).then((results) => {
        const [tableCountRows, databaseStats] = results;
        const databases = databaseStats.map((result) => {
            const row = tableCountRows.find((row) => row.databaseName === result.databaseName);
            let tableCount = 0;
            if (row) {
                tableCount = row.count.toNumber();
            }
            const statisticsData = databaseStats.find((row) => row.databaseName === result.databaseName);
            let statistics = undefined;
            if (statisticsData) {
                let syncRepl;
                if (statisticsData.syncRepl !== null) {
                    syncRepl = statisticsData.syncRepl.gt(0);
                }
                statistics = {
                    partitionCount: new LESuccess(statisticsData.partitionCount),
                    syncRepl,
                    remoteName: statisticsData.remoteName || undefined,
                };
            }
            return {
                kind: "DATABASE",
                databaseName: result.databaseName,
                statistics,
                tableCount,
            };
        });
        return {
            databases: new LESuccess(databases),
        };
    });
}
function fetchAndEmitSchemaStructure({ observer, getConnection, databaseName, }) {
    return fetchSchemaStructure({ getConnection, databaseName })
        .then((structure) => {
        return createStructureUpdateAction(structure);
    })
        .then((structureAction) => {
        observer.next(structureAction);
    });
}
function fetchAndEmitDatabasesStructure({ observer, getConnection, }) {
    return fetchDatabasesStructure(getConnection)
        .then((structure) => {
        return structure;
    })
        .then((databasesStructure) => {
        if (!databasesStructure.databases.isSuccess()) {
            throw new Error("Expected databases to be loaded");
        }
        observer.next(createDatabasesStructureAction(databasesStructure));
        return databasesStructure.databases.value.map((database) => database.databaseName);
    });
}
export const queryDatabasesStructure = makeActionCreator({
    name: "queryDatabasesStructure",
    handle: (ctx) => {
        const $loading = of({
            type: "DATABASES_STRUCTURE",
            error: false,
            payload: { loading: true },
        });
        const $compute = Observable.create((observer) => {
            return fetchAndEmitDatabasesStructure({
                observer,
                getConnection: () => ctx.manager.getPooledConnection(),
            }).finally(() => {
                observer.complete();
            });
        });
        return merge($loading, $compute);
    },
});
export const queryDatabaseStructure = makeActionCreator({
    name: "queryDatabaseStructure",
    handle: (ctx, { databaseName }) => {
        const $compute = Observable.create((observer) => {
            return fetchAndEmitSchemaStructure({
                observer,
                getConnection: () => ctx.manager.getPooledConnection(),
                databaseName,
            }).finally(() => {
                observer.complete();
            });
        });
        return $compute;
    },
});
export const queryStructure = makeActionCreator({
    name: "queryStructure",
    handle: (ctx) => {
        const $loading = of({
            type: "SCHEMA_STRUCTURE",
            error: false,
            payload: { loading: true },
        });
        const $compute = Observable.create((observer) => {
            return fetchAndEmitDatabasesStructure({
                observer,
                getConnection: () => ctx.manager.getPooledConnection(),
            })
                .then((databaseNames) => {
                return fetchAndEmitSchemaStructure({
                    observer,
                    getConnection: () => ctx.manager.getPooledConnection(),
                })
                    .then(() => {
                    return fetchAndEmitExtraSchemaStructure({
                        observer,
                        getConnection: () => ctx.manager.getPooledConnection(),
                        databaseNames,
                    });
                })
                    .catch((err) => {
                    observer.next({
                        type: "SCHEMA_STRUCTURE",
                        error: true,
                        payload: {
                            message: err.message,
                        },
                    });
                });
            })
                .finally(() => {
                observer.complete();
            });
        });
        return merge($loading, $compute);
    },
});
function fetchClusterStatistics(conn) {
    return select(conn, SQL_STATISTICS_HA).then((sqlClusterHA) => {
        if (sqlClusterHA.length !== 1) {
            throw new Error("This query should only return one row");
        }
        return {
            haEnabled: Number(sqlClusterHA[0].Value) >= 2, // boolean
        };
    });
}
const createClusterStatisticsAction = (data) => {
    return {
        type: "CLUSTER_STATISTICS",
        error: false,
        payload: {
            loading: false,
            data,
        },
    };
};
// See PLAT-508 for more details.
const filterIndexes = (sqlIndexesStats, sqlColumnarIndexesStats) => {
    let indexes = {};
    for (let i = 0; i < sqlIndexesStats.length; i++) {
        const index = sqlIndexesStats[i];
        const hashedIndex = [
            index.databaseName,
            index.tableName,
            index.indexName,
            index.indexType,
            index.nonUnique,
        ].join(".");
        const savedIndex = indexes[hashedIndex];
        const columnarIndex = sqlColumnarIndexesStats.filter((sqlColumnarIndex) => index.databaseName === sqlColumnarIndex.databaseName &&
            index.tableName === sqlColumnarIndex.tableName &&
            index.indexName === sqlColumnarIndex.indexName);
        let diskUse = undefined;
        if (columnarIndex.length > 1) {
            // This is not supposed to happen. For the combination databaseName,
            // tableName, indexName only one entrance should be found in the
            // table MV_COLUMNAR_SEGMENT_INDEX
            logError(new Error(`Multiple entries were found for a column hash index with the following combination: databaseName, tableName, indexName`));
        }
        else if (columnarIndex.length === 1) {
            diskUse = columnarIndex[0].totalSize;
        }
        if (savedIndex) {
            savedIndex.columnNames.push(index.columnName);
        }
        else {
            indexes[hashedIndex] = {
                kind: "INDEX",
                indexId: hashedIndex,
                databaseName: index.databaseName,
                tableName: index.tableName,
                indexName: index.indexName,
                memoryUse: index.memoryUse,
                diskUse,
                indexType: index.indexType,
                columnNames: [index.columnName],
                // if non unique is false, then unique is true
                unique: index.nonUnique.isZero(),
            };
        }
    }
    // SHARD indices can appear in information_schema.STATISTICS with the
    // same name as a different index; if this is the case, they actually
    // are just an alias for the other index, and therefore the SHARD
    // index's memory use should be 0.
    indexes = _.mapValues(indexes, (index) => {
        if (index.indexType === "SHARD") {
            if (_.find(indexes, (otherIndex) => otherIndex.databaseName === index.databaseName &&
                otherIndex.tableName === index.tableName &&
                otherIndex.indexName === index.indexName &&
                otherIndex.indexType !== index.indexType)) {
                return {
                    ...index,
                    memoryUse: new BigNumber(0),
                };
            }
        }
        return index;
    });
    return _.values(indexes);
};
function createColumnarSegmentsAction(sqlColumnarStatistics) {
    return {
        type: "SCHEMA_COLUMNAR_SEGMENTS",
        error: false,
        payload: sqlColumnarStatistics,
    };
}
function createColumnStatisticsAction(sqlColumnStatistics) {
    return {
        type: "SCHEMA_COLUMN_STATISTICS",
        error: false,
        payload: sqlColumnStatistics,
    };
}
const createStatisticsAction = (sqlTableMemoryStats, startDate) => {
    return {
        type: "SCHEMA_STATISTICS",
        error: false,
        payload: {
            loading: false,
            data: {
                tableStats: sortTables(sqlTableMemoryStats),
                indexes: new LELoading(),
                deltaTimeS: differenceInSeconds(new Date(), startDate),
            },
        },
    };
};
function fetchAndEmitExtraSchemaStructure({ observer, getConnection, databaseNames, }) {
    const functionsPromise = getConnection().then((conn) => queryFunctions(conn)({ databaseNames })
        .then((userDefinedFunctions) => {
        observer.next({
            type: "SCHEMA_STRUCTURE_UPDATE",
            error: false,
            payload: {
                userDefinedFunctions: new LESuccess(userDefinedFunctions),
            },
        });
    })
        .finally(() => {
        conn.release();
    }));
    const storedProceduresPromise = getConnection().then((conn) => queryStoredProcedures(conn)({ databaseNames })
        .then((storedProcedures) => {
        observer.next({
            type: "SCHEMA_STRUCTURE_UPDATE",
            error: false,
            payload: {
                storedProcedures: new LESuccess(storedProcedures),
            },
        });
    })
        .finally(() => {
        conn.release();
    }));
    const aggregatesPromise = getConnection().then((conn) => queryAggregates(conn)({ databaseNames })
        .then((aggregates) => {
        observer.next({
            type: "SCHEMA_STRUCTURE_UPDATE",
            error: false,
            payload: {
                aggregates: new LESuccess(aggregates),
            },
        });
    })
        .finally(() => {
        conn.release();
    }));
    return Promise.all([
        functionsPromise,
        storedProceduresPromise,
        aggregatesPromise,
    ]);
}
function fetchAndEmitSchemaStatistics({ conn, observer, startDate, }) {
    return select(conn, SQL_STATISTICS_TABLE).then((results) => {
        // Map all the results into the real result and
        // throw an exception if an error happened. The last
        // result SQLStatisticsIndexColumnar has a special
        // threatment when the table doesn't exist, so we
        // can support engines < 7.0.
        const mappedResults = mapMultiSelectErrorResult(results);
        observer.next(createStatisticsAction(mappedResults, startDate));
    });
}
function fetchAndEmitExtraSchemaStatistics({ observer, getConnection, }) {
    return multiSelectParallelWithErrors({
        getConnection,
        queries: [
            {
                sql: SQL_STATISTICS_COLUMNS_COLUMNAR,
            },
            {
                sql: SQL_STATISTICS_COLUMNS_MEMORY,
            },
            { sql: SQL_STATISTICS_INDEXES },
            { sql: SQL_STATISTICS_INDEXES_COLUMNAR },
        ],
    }).then(([columnarSegments, columnStatistics, sqlIndexesStats, sqlColumnarIndexesStats,]) => {
        if (columnarSegments instanceof Error) {
            observer.next(logFocusError(`An error occured while querying information_schema.columnar_segments: ${columnarSegments.message}`));
            observer.next({
                type: "SCHEMA_COLUMNAR_SEGMENTS",
                error: true,
                payload: columnarSegments.message,
            });
        }
        else {
            observer.next(createColumnarSegmentsAction(columnarSegments));
        }
        if (columnStatistics instanceof Error) {
            observer.next(logFocusError(`An error occured while querying information_schema.column_statistics: ${columnStatistics.message}`));
            observer.next({
                type: "SCHEMA_COLUMN_STATISTICS",
                error: true,
                payload: columnStatistics.message,
            });
        }
        else {
            observer.next(createColumnStatisticsAction(columnStatistics));
        }
        if (sqlIndexesStats instanceof Error) {
            logging.log("error", "An error occurred while loading SQL_STATISTICS_INDEXES", sqlIndexesStats);
            observer.next({
                type: "INDEXES",
                error: true,
                payload: `An error occurred while loading the indexes data: ${sqlIndexesStats}`,
            });
        }
        else if (sqlColumnarIndexesStats instanceof Error) {
            if ("code" in sqlColumnarIndexesStats &&
                sqlColumnarIndexesStats.code === "ER_NO_SUCH_TABLE") {
                // If `sqlColumnarIndexesStats` fails with ER_NO_SUCH_TABLE,
                // we assume there's no columnstore secondary indexes since
                // the table that tracks this didn't exist in older MemSQL
                // versions.
                logging.log("debug", "Grabbing information about columnstore secondary indexes failed because the table doesn't exist.");
                observer.next({
                    type: "INDEXES",
                    error: false,
                    payload: filterIndexes(sqlIndexesStats, []),
                });
            }
            else {
                logging.log("error", "An error occurred while loading SQL_STATISTICS_INDEXES_COLUMNAR", sqlColumnarIndexesStats);
                observer.next({
                    type: "INDEXES",
                    error: true,
                    payload: `An error occurred while loading the indexes data: ${sqlColumnarIndexesStats}`,
                });
            }
        }
        else {
            observer.next({
                type: "INDEXES",
                error: false,
                payload: filterIndexes(sqlIndexesStats, sqlColumnarIndexesStats),
            });
        }
    });
}
export const querySchemaStatistics = makeActionCreator({
    name: "querySchemaStatistics",
    handle(ctx) {
        const $loadingClusterStatistics = of({
            type: "CLUSTER_STATISTICS",
            error: false,
            payload: { loading: true },
        });
        const $loadingSchemaStatistics = of({
            type: "SCHEMA_STATISTICS",
            error: false,
            payload: { loading: true },
        });
        const startDate = new Date();
        const $compute = Observable.create((observer) => {
            ctx.manager
                .getPooledConnection()
                .then((conn) => fetchClusterStatistics(conn)
                .then(createClusterStatisticsAction)
                .then((out) => {
                observer.next(out);
            })
                .then(() => {
                return fetchAndEmitDatabasesStructure({
                    observer,
                    getConnection: () => ctx.manager.getPooledConnection(),
                });
            })
                .then((databaseNames) => {
                return fetchAndEmitSchemaStructure({
                    observer,
                    getConnection: () => ctx.manager.getPooledConnection(),
                }).then(() => {
                    return Promise.all([
                        fetchAndEmitExtraSchemaStructure({
                            observer,
                            getConnection: () => ctx.manager.getPooledConnection(),
                            databaseNames,
                        }),
                        fetchAndEmitSchemaStatistics({
                            observer,
                            startDate,
                            conn,
                        }).then(() => {
                            return fetchAndEmitExtraSchemaStatistics({
                                getConnection: () => ctx.manager.getPooledConnection(),
                                observer,
                            });
                        }),
                    ]);
                });
            })
                .catch((err) => {
                observer.next({
                    type: "SCHEMA_STATISTICS",
                    error: true,
                    payload: {
                        message: err.message,
                    },
                });
            })
                .finally(() => {
                return conn.release();
            }))
                .catch((err) => {
                observer.next({
                    type: "SCHEMA_STATISTICS",
                    error: true,
                    payload: {
                        message: err.message,
                    },
                });
            })
                .finally(() => observer.complete());
        });
        return merge($loadingClusterStatistics, $loadingSchemaStatistics, $compute);
    },
});
const createSchemaSummaryAction = ([sqlDatabasesDiskUsage, sqlMemoryUsage, sqlAvailableMemory, sqlTotalMemory, sqlDiskUsage, sqlTotalDisk, sqlDatabaseNames,], startDate) => {
    if (sqlMemoryUsage.length !== 1) {
        throw new Error("Expected query to fetch memory usage to be a single row.");
    }
    if (sqlAvailableMemory.length !== 1) {
        throw new Error("Expected query to fetch memory available to be a single row.");
    }
    if (sqlTotalMemory.length !== 1) {
        throw new Error("Expected query to fetch total memory to be a single row.");
    }
    if (sqlDiskUsage.length !== 1) {
        throw new Error("Expected query to fetch disk usage to be a single row.");
    }
    if (sqlTotalDisk.length !== 1) {
        throw new Error("Expected query to fetch total disk available to be a single row.");
    }
    const topDiskDatabases = _(sqlDatabasesDiskUsage)
        .take(3)
        .filter((d) => d.diskUsage.gt(0))
        .map((d) => ({
        name: d.databaseName,
        diskUsage: d.diskUsage,
    }))
        .value();
    if (sqlMemoryUsage[0].memoryUsageB === null) {
        return {
            type: "SCHEMA_SUMMARY",
            error: true,
            payload: "An error occurred while calculating memory usage",
        };
    }
    if (sqlAvailableMemory[0].availableMemoryB === null) {
        return {
            type: "SCHEMA_SUMMARY",
            error: true,
            payload: "An error occurred while calculating available memory",
        };
    }
    if (sqlTotalMemory[0].totalMemoryB === null) {
        return {
            type: "SCHEMA_SUMMARY",
            error: true,
            payload: "An error occurred while calculating total memory",
        };
    }
    if (sqlDiskUsage[0].diskUsageB === null) {
        return {
            type: "SCHEMA_SUMMARY",
            error: true,
            payload: "An error occurred while calculating disk usage",
        };
    }
    if (sqlTotalDisk[0].diskTotalB === null) {
        return {
            type: "SCHEMA_SUMMARY",
            error: true,
            payload: "An error occurred while calculating total disk",
        };
    }
    return {
        type: "SCHEMA_SUMMARY",
        error: false,
        payload: {
            loading: false,
            data: {
                memoryUsage: new BigNumber(sqlMemoryUsage[0].memoryUsageB),
                memoryAvailable: sqlAvailableMemory[0].availableMemoryB,
                memoryTotal: sqlTotalMemory[0].totalMemoryB,
                diskUsage: sqlDiskUsage[0].diskUsageB,
                diskTotal: sqlTotalDisk[0].diskTotalB,
                topDiskDatabases,
                databaseNames: _.map(sqlDatabaseNames, ({ databaseName }) => databaseName),
                deltaTimeS: differenceInSeconds(new Date(), startDate),
            },
        },
    };
};
export const querySchemaSummary = makeActionCreator({
    name: "querySchemaSummary",
    handle(ctx) {
        const $loadingSummary = of({
            type: "SCHEMA_SUMMARY",
            error: false,
            payload: { loading: true },
        });
        const startDate = new Date();
        const $compute = from(multiSelectParallel({
            getConnection: () => ctx.manager.getPooledConnection(),
            queries: [
                { sql: SQL_DATABASE_DISK_USAGE },
                { sql: SQL_MEMORY_USAGE },
                { sql: SQL_AVAILABLE_MEMORY },
                { sql: SQL_TOTAL_MEMORY },
                { sql: SQL_DISK_USAGE },
                { sql: SQL_TOTAL_DISK },
                { sql: SQL_DATABASE_NAMES },
            ],
        })
            .then((results) => createSchemaSummaryAction(results, startDate))
            .catch((err) => {
            return {
                type: "SCHEMA_SUMMARY",
                error: true,
                payload: err.message,
            };
        }));
        return merge($loadingSummary, $compute);
    },
});
export const queryLightSchemaSummary = makeActionCreator({
    name: "queryLightSchemaSummary",
    handle(ctx) {
        const $loadingSummary = of({
            type: "SCHEMA_SUMMARY",
            error: false,
            payload: { loading: true },
        });
        const startDate = new Date();
        const $compute = from(multiSelectParallel({
            getConnection: () => ctx.manager.getPooledConnection(),
            queries: [
                { sql: SQL_MEMORY_USAGE },
                { sql: SQL_AVAILABLE_MEMORY },
                { sql: SQL_TOTAL_MEMORY },
                { sql: SQL_DISK_USAGE },
                { sql: SQL_TOTAL_DISK },
                { sql: SQL_DATABASE_NAMES },
            ],
        })
            .then((results) => createSchemaSummaryAction([undefined, ...results], startDate))
            .catch((err) => {
            return {
                type: "SCHEMA_SUMMARY",
                error: true,
                payload: err.message,
            };
        }));
        return merge($loadingSummary, $compute);
    },
});
export const queryTableLikeSample = makeActionCreator({
    name: "queryTableLikeSample",
    handle(ctx, { databaseName, tableName, }) {
        const $loading = of({
            type: "TABLE_LIKE_SAMPLE",
            error: false,
            payload: { loading: true },
        });
        const $compute = from(ctx.manager
            .getPooledConnection()
            .then((conn) => {
            return new Promise((resolve, reject) => conn.query({
                sql: SQL_TABLE_LIKE_SAMPLE,
                nestTables: true,
                values: [databaseName, tableName],
                // We don't want to typecast the output of this query since we
                // just display the results as strings directly to the user.
                typeCast: false,
            }, (error, rows, fields) => {
                if (error) {
                    return reject(error);
                }
                else if (!fields) {
                    logError(new Error("Got back no fields while querying for sample data from a table."));
                    return reject(new Error("An error occured while fetching sample data from this table."));
                }
                else {
                    return resolve({
                        type: "TABLE_LIKE_SAMPLE",
                        error: false,
                        payload: {
                            loading: false,
                            data: {
                                sampleRows: _.map(rows, (row) => rawResultToArray(fields, row)),
                                fields,
                            },
                        },
                    });
                }
            }))
                .catch((err) => {
                logError(err);
                return {
                    type: "TABLE_LIKE_SAMPLE",
                    error: true,
                    payload: {
                        message: err.message,
                    },
                };
            })
                .finally(() => conn.release());
        })
            .catch((err) => {
            return {
                type: "TABLE_LIKE_SAMPLE",
                error: true,
                payload: {
                    message: err.message,
                },
            };
        }));
        return merge($loading, $compute);
    },
});
export const queryShowCreateTableLikeSql = makeActionCreator({
    name: "queryShowCreateTableLikeSql",
    handle(ctx, { databaseName, tableName, kind }) {
        const $loading = of({
            type: "SHOW_CREATE_TABLE_LIKE_SQL",
            error: false,
            payload: { loading: true },
        });
        const $compute = from(ctx.manager
            .getPooledConnection()
            .then((conn) => {
            return new Promise((resolve, reject) => conn.query({
                sql: stripSlashG(getShowCreateTableQuery({
                    databaseName,
                    tableName,
                    kind,
                })),
                nestTables: true,
            }, (error, rows, fields) => {
                if (error) {
                    return reject(error);
                }
                else if (!fields) {
                    logError(new Error("Got back no fields while querying for show create table."));
                    return reject(new Error("An error occurred while fetching the SHOW CREATE statement."));
                }
                else {
                    return resolve({
                        type: "SHOW_CREATE_TABLE_LIKE_SQL",
                        error: false,
                        payload: {
                            loading: false,
                            data: {
                                rows: _.map(rows, (row) => rawResultToArray(fields, row)),
                                fields,
                            },
                        },
                    });
                }
            }))
                .catch((err) => {
                return {
                    type: "SHOW_CREATE_TABLE_LIKE_SQL",
                    error: true,
                    payload: { message: err.message },
                };
            })
                .finally(() => conn.release());
        })
            .catch((err) => {
            return {
                type: "SHOW_CREATE_TABLE_LIKE_SQL",
                error: true,
                payload: { message: err.message },
            };
        }));
        return merge($loading, $compute);
    },
});
